import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent, CartUpdateEvent } from '@theme/events';
import { yieldToMainThread, getViewParameterValue, isMobileBreakpoint } from '@theme/utilities';
import { morph, MORPH_OPTIONS } from '@theme/morph';

/**
 * @typedef {object} BbdVariantPickerRefs
 * @property {HTMLDivElement} dropdown
 * @property {HTMLDivElement} [mobileTitle]
 * @property {HTMLSpanElement} selectedText
 * @property {HTMLInputElement} variantInput
 * @property {HTMLButtonElement} [addToCartButton]
 */

/**
 * A custom element that manages a dropdown variant picker.
 *
 * @extends Component<BbdVariantPickerRefs>
 */
export default class BbdVariantPicker extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Set<string>} option-group indices the customer has explicitly picked a value for */
  #selectedGroupIndices = new Set();

  /** @type {Set<string>} option-group indices showing the "select your size" hint (in memory so it survives morph) */
  #hintGroupIndices = new Set();

  /** @type {number | undefined} timeStamp of the click that opened a group from outside, so #onDocumentClick skips it */
  #externalOpenTimeStamp;

  /** @type {boolean} true while the most-popular auto-select's section fetch is still in flight */
  #isAutoSelecting = false;

  /**
   * Whether the most-popular auto-select is still waiting for its VariantUpdateEvent.
   * Read by bbd-datalayer-ecommerce.js to fire a single view_item for the variant that ends up selected.
   * @returns {boolean}
   */
  get isAutoSelecting() {
    return this.#isAutoSelecting;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('keydown', this.#onKeyDown);
    this.#autoSelectMostPopular();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#onDocumentClick);
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  #onDocumentClick = (event) => {
    if (event.timeStamp === this.#externalOpenTimeStamp) return;

    const openGroup = this.querySelector('.bbd-variant-picker__option-group.is-open');
    if (!openGroup) return;
    if (openGroup.contains(/** @type {Node} */ (event.target))) return;

    this.close();
  };

  #onKeyDown = (event) => {
    const group = event.target.closest('.bbd-variant-picker__option-group');
    if (!group) return;

    const isOpen = group.classList.contains('is-open');

    if (event.key === 'Escape' && isOpen) {
      this.close();
      group.querySelector('.bbd-variant-picker__toggle')?.focus();
      return;
    }

    if (isOpen) {
      const options = Array.from(group.querySelectorAll('.bbd-variant-picker__option'));
      const currentIndex = options.indexOf(document.activeElement);

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % options.length;
        options[nextIndex].focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = (currentIndex - 1 + options.length) % options.length;
        options[prevIndex].focus();
      }
    }
  };

  toggle(event) {
    const group = event.target.closest('.bbd-variant-picker__option-group');
    if (!group) return;

    const toggle = group.querySelector('.bbd-variant-picker__toggle');
    const isOpen = group.classList.contains('is-open');

    this.close(); // Close any other open dropdowns

    if (!isOpen) {
      this.classList.add('is-open');
      group.classList.add('is-open');
      toggle?.setAttribute('aria-expanded', 'true');

      if (isMobileBreakpoint()) {
        const isInModal = !!this.closest('quick-add-dialog');
        if (!isInModal) {
          document.body.style.overflow = 'hidden';
        }
      } else {
        // Focus the first option or the selected option
        const selectedOption = group.querySelector('.bbd-variant-picker__option.is-selected');
        const firstOption = group.querySelector('.bbd-variant-picker__option');
        (selectedOption || firstOption)?.focus();
      }
    }
  }

  close() {
    this.classList.remove('is-open');
    this.querySelectorAll('.bbd-variant-picker__option-group').forEach((group) => {
      group.classList.remove('is-open');
      group.querySelector('.bbd-variant-picker__toggle')?.setAttribute('aria-expanded', 'false');
    });
    const isInModal = !!this.closest('quick-add-dialog');
    if (!isInModal) {
      document.body.style.overflow = '';
    }
  }

  /**
   * Opens the first option group (in DOM order) the customer hasn't explicitly
   * clicked a value in yet, including groups Shopify pre-filled with a default
   * value, and covering bundles with several still-incomplete items. Used to
   * force a conscious choice when triggered from outside (e.g. sticky add-to-cart).
   * @param {Event} [triggerEvent] - The outside click that triggered this, ignored by the outside-click close handler.
   * @returns {boolean} whether an incomplete group was opened
   */
  openFirstIncompleteGroup(triggerEvent) {
    const groups = Array.from(
      this.querySelectorAll('.bbd-variant-picker__option-group:not(.bbd-variant-picker__option-group--static)')
    );
    const target = groups.find((group) => !this.#selectedGroupIndices.has(group.dataset.optionIndex));
    if (!target) return false;

    this.#externalOpenTimeStamp = triggerEvent?.timeStamp;

    if (target.dataset.optionIndex) this.#hintGroupIndices.add(target.dataset.optionIndex);
    this.#renderHints();

    this.close();
    this.classList.add('is-open');
    target.classList.add('is-open');
    target.querySelector('.bbd-variant-picker__toggle')?.setAttribute('aria-expanded', 'true');

    if (isMobileBreakpoint() && !this.closest('quick-add-dialog')) {
      document.body.style.overflow = 'hidden';
    } else if (!isMobileBreakpoint()) {
      target.querySelector('.bbd-variant-picker__option')?.focus();
    }

    return true;
  }

  /**
   * Shows the "select your size" hint on groups in #hintGroupIndices and clears it elsewhere.
   * Re-run after morph, which resets the hints to the server's empty markup.
   */
  #renderHints() {
    this.querySelectorAll('.bbd-variant-picker__option-group').forEach((group) => {
      const show = this.#hintGroupIndices.has(group.dataset.optionIndex);

      // Inline hint (desktop) and bottom-sheet hint (mobile); CSS shows one per breakpoint
      group.querySelectorAll('.bbd-variant-picker__hint').forEach((hint) => {
        if (!(hint instanceof HTMLElement)) return;
        hint.textContent = show ? (hint.dataset.message ?? '') : '';
      });
    });
  }

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {object | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    let newProduct;
    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Keep track of which dropdowns were open
    const openGroupIndices = Array.from(this.querySelectorAll('.bbd-variant-picker__option-group'))
      .map((group, index) => (group.classList.contains('is-open') ? index : null))
      .filter((index) => index !== null);

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        return node.dataset.key;
      }
    });

    // Restore open state
    if (openGroupIndices.length > 0) {
      this.classList.add('is-open');
    }
    openGroupIndices.forEach((index) => {
      const groups = this.querySelectorAll('.bbd-variant-picker__option-group');
      if (groups[index]) {
        groups[index].classList.add('is-open');
        groups[index].querySelector('.bbd-variant-picker__toggle')?.setAttribute('aria-expanded', 'true');
      }
    });

    this.#renderHints();

    return newProduct;
  }

  /**
   * Updates the selectedText element for a group to reflect the given option value.
   * @param {Element} group - The option group element.
   * @param {string} optionValue - The selected option value.
   * @param {Element | null} [selectedOption] - The selected option element (used to read sold-out and popular label state).
   */
  #updateSelectedText(group, optionValue, selectedOption) {
    const selectedText = group.querySelector('[ref="selectedText"]');
    if (!selectedText) return;

    const valParts = optionValue.split(' ');
    const valFirstPart = valParts[0];
    const valFirstChar = valFirstPart ? valFirstPart.charAt(0) : '';
    const hasSizeDefinition = valParts.length > 1 && ['S', 'M', 'L', 'X'].includes(valFirstChar);

    let innerHTML = '';
    if (hasSizeDefinition) {
      const sizeValue = optionValue.replace(valFirstPart, '').trim();
      innerHTML = `<span class="bbd-variant-picker__option-size-definition">${valFirstPart}</span><span class="bbd-variant-picker__option-size-value">${sizeValue}</span>`;
    } else {
      innerHTML = optionValue;
    }

    const option =
      selectedOption ??
      group.querySelector(`.bbd-variant-picker__option[data-option-value="${CSS.escape(optionValue)}"]`);
    const isUnavailable = option?.classList.contains('is-unavailable') ?? false;
    const soldOutLabel = option?.querySelector('.bbd-variant-picker__option-sold-out');
    const soldOutText = soldOutLabel ? soldOutLabel.textContent : '';

    innerHTML += `<span class="bbd-variant-picker__option-sold-out" ref="soldOutLabel" style="display: ${isUnavailable ? 'inline' : 'none'};">${soldOutText}</span>`;

    const popularLabel = option?.querySelector('.bbd-variant-picker__option-label');
    if (popularLabel) {
      innerHTML += popularLabel.outerHTML;
    }

    selectedText.innerHTML = innerHTML;
  }

  selectOption(event) {
    const option = event.target.closest('.bbd-variant-picker__option');
    if (!option) return;

    const group = option.closest('.bbd-variant-picker__option-group');
    if (!group) return;

    if (group.dataset.optionIndex) {
      this.#selectedGroupIndices.add(group.dataset.optionIndex);
      this.#hintGroupIndices.delete(group.dataset.optionIndex);
      this.#renderHints();
    }

    const optionValue = option.dataset.optionValue;

    // Update selection UI for this group
    group.querySelectorAll('.bbd-variant-picker__option').forEach((opt) => {
      const isSelected = opt === option;
      opt.classList.toggle('is-selected', isSelected);
      opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
    });

    this.#updateSelectedText(group, optionValue, option);

    // Resolve variant
    this.#updateVariant();

    this.classList.add('has-selection');

    // Close after selection and return focus to toggle (only on desktop)
    if (window.innerWidth >= 750) {
      this.close();
      group.querySelector('.bbd-variant-picker__toggle')?.focus();
    }
  }

  #autoSelectMostPopular() {
    const isInModal = !!this.closest('quick-add-dialog');

    // On PDP: skip if the user explicitly navigated to a specific variant via the URL.
    // In the modal the browser URL is unrelated to the fetched product, so this check is skipped.
    if (!isInModal && new URLSearchParams(window.location.search).has('variant')) return;

    const data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);

    // For bundle products, is_most_popular is set on individual sub-product variants rather than
    // the combined bundle product variant. Fall back to per-option-group values from sub-products.
    const hasBundleMostPopular = data.bundle_most_popular_values?.some((v) => v !== null);

    if (!data.most_popular_variant_id && !hasBundleMostPopular) return;

    let getTargetValue;

    if (data.most_popular_variant_id) {
      // Skip if the server already rendered with the most popular variant selected.
      if (data.selected_variant?.id === data.most_popular_variant_id) return;

      const mostPopularVariant = data.variants.find((v) => v.id === data.most_popular_variant_id);
      if (!mostPopularVariant) return;

      getTargetValue = (index) => mostPopularVariant[`option${index + 1}`];
    } else {
      // Fallback: derive per-group target values from bundle sub-products' is_most_popular.
      getTargetValue = (index) => data.bundle_most_popular_values?.[index] ?? null;
    }

    const groups = this.querySelectorAll('.bbd-variant-picker__option-group');
    groups.forEach((group, index) => {
      const targetValue = getTargetValue(index);
      if (!targetValue) return;

      group.querySelectorAll('.bbd-variant-picker__option').forEach((opt) => {
        const isSelected = opt.dataset.optionValue === targetValue;
        opt.classList.toggle('is-selected', isSelected);
        opt.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      });

      this.#updateSelectedText(group, targetValue);
    });

    this.#isAutoSelecting = true;
    this.#updateVariant();
  }

  #updateVariant() {
    // Exclude static bundle groups — they have no interactive options and would produce null
    // entries that prevent matching any variant.
    const selectedOptions = Array.from(
      this.querySelectorAll('.bbd-variant-picker__option-group:not(.bbd-variant-picker__option-group--static)')
    ).map((group) => {
      const selected = group.querySelector('.bbd-variant-picker__option.is-selected');
      return selected ? selected.dataset.optionValue : null;
    });

    const data = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
    const variants = data.variants;
    const translations = data.translations || {};

    const matchedVariant = variants.find((variant) => {
      return selectedOptions.every((value, index) => {
        return variant[`option${index + 1}`] === value;
      });
    });

    // Update availability for all interactive options (static groups have no options to update)
    this.querySelectorAll('.bbd-variant-picker__option-group:not(.bbd-variant-picker__option-group--static)').forEach(
      (group) => {
        const options = group.querySelectorAll('.bbd-variant-picker__option');
        options.forEach((option) => {
          const optionValue = option.dataset.optionValue;

          const isAvailable = variants.some((variant) => {
            return variant.options.includes(optionValue) && variant.available;
          });
          option.classList.toggle('is-unavailable', !isAvailable);

          const soldOutLabel = option.querySelector('.bbd-variant-picker__option-sold-out');
          if (soldOutLabel) {
            soldOutLabel.style.display = isAvailable ? 'none' : 'inline';
          }

          // We also need to find a first available variant with this value to update labels if needed.
          // But the labels are currently baked into the HTML and not updated dynamically here yet beyond sold out.
          // If we want to update labels, we'd need more logic. For now, let's stick to sold-out status as requested.

          // If this option is selected, also update the toggle's sold out label
          if (option.classList.contains('is-selected')) {
            const toggleSoldOutLabel = group.querySelector('.bbd-variant-picker__toggle [ref="soldOutLabel"]');
            if (toggleSoldOutLabel) {
              toggleSoldOutLabel.style.display = isAvailable ? 'none' : 'inline';
            }
          }
        });
      }
    );

    if (matchedVariant) {
      if (this.refs.variantInput) {
        this.refs.variantInput.value = matchedVariant.id;
        this.refs.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.querySelectorAll('[ref="addToCartButton"]').forEach((button) => {
        button.hidden = !matchedVariant.available;
      });

      const bisTrigger = this.querySelector('.bbd-back-in-stock__trigger');
      if (bisTrigger) {
        bisTrigger.dataset.bisVariantId = String(matchedVariant.id);
        bisTrigger.hidden = matchedVariant.available;
      }

      this.querySelectorAll('.bbd-variant-picker__option-group').forEach((group) => {
        const mobileTitle = group.querySelector('[ref="mobileTitle"]');
        const selected = group.querySelector('.bbd-variant-picker__option.is-selected');
        const allGroups = this.querySelectorAll('.bbd-variant-picker__option-group');
        if (mobileTitle && selected) {
          const prefix = translations.choose || mobileTitle.dataset.prefix || 'Choose';
          const optionName = allGroups.length > 1 ? ` ${selected.dataset.optionValue}` : '';
          mobileTitle.textContent = `${prefix}${optionName}`;
        }
      });

      this.dispatchEvent(new VariantSelectedEvent({ id: matchedVariant.id }));

      const isInModal = !!this.closest('quick-add-dialog');
      const baseProductUrl = this.dataset.productUrl || window.location.pathname;
      const url = new URL(baseProductUrl, window.location.origin);
      url.searchParams.set('variant', matchedVariant.id);

      if (!isInModal && url.href !== window.location.href) {
        yieldToMainThread().then(() => {
          history.replaceState({}, '', url.toString());
        });
      }

      this.fetchUpdatedSection(this.buildRequestUrl(matchedVariant.id), matchedVariant.id);
    } else {
      this.#isAutoSelecting = false;
    }
  }

  /**
   * Builds the request URL for section rendering.
   * @param {string} variantId - The selected variant ID.
   * @returns {string} The request URL.
   */
  buildRequestUrl(variantId) {
    const productUrl = this.dataset.productUrl ? this.dataset.productUrl.split('?')[0] : window.location.pathname;
    const params = [];
    const viewParamValue = getViewParameterValue();

    if (viewParamValue) params.push(`view=${viewParamValue}`);

    params.push(`variant=${variantId}`);

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section HTML and dispatches VariantUpdateEvent.
   * @param {string} requestUrl - The request URL.
   * @param {string} variantId - The selected variant ID.
   */
  fetchUpdatedSection(requestUrl, variantId) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');

        // The page also holds the cart drawer, and its look cards have quick-add forms. product-form.js takes the first
        // `product-form-component [ref="addToCartButton"]` of this document as the new add to cart button, which is
        // the drawer's button when it is not removed here.
        html.querySelectorAll('cart-drawer-component').forEach((drawer) => drawer.remove());

        const textContent = html.querySelector('bbd-variant-picker script[type="application/json"]')?.textContent;

        if (!textContent) return;

        const data = JSON.parse(textContent);
        const newProduct = this.updateVariantPicker(html);

        // After morph, if we're on mobile and a dropdown was open, ensure it stays open correctly.
        // The `updateVariantPicker` method already attempts to restore open state by index.

        this.dispatchEvent(
          new VariantUpdateEvent(data.selected_variant, variantId, {
            html,
            productId: this.dataset.productId ?? '',
            newProduct
          })
        );
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted');
        } else {
          console.error(error);
        }
      })
      .finally(() => {
        this.#isAutoSelecting = false;
      });
  }

  #embedYouTubeLinks(container) {
    // Replace <a> tags pointing to YouTube
    container.querySelectorAll('a[href]').forEach((anchor) => {
      const videoId = this.#extractYouTubeId(anchor.href);
      if (!videoId) return;
      anchor.replaceWith(this.#createYouTubeIframe(videoId));
    });

    // Replace plain-text YouTube URLs in text nodes
    const youtubePattern = /https?:\/\/(?:www\.)?(?:youtube\.com\/watch\?[^\s]*v=[\w-]+|youtu\.be\/[\w-]+)[^\s]*/g;
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    for (const node of textNodes) {
      if (!youtubePattern.test(node.textContent)) continue;
      youtubePattern.lastIndex = 0;

      const fragment = document.createDocumentFragment();
      let last = 0;
      let match;
      while ((match = youtubePattern.exec(node.textContent)) !== null) {
        if (match.index > last)
          fragment.appendChild(document.createTextNode(node.textContent.slice(last, match.index)));
        const videoId = this.#extractYouTubeId(match[0]);
        fragment.appendChild(videoId ? this.#createYouTubeIframe(videoId) : document.createTextNode(match[0]));
        last = match.index + match[0].length;
      }
      if (last < node.textContent.length) fragment.appendChild(document.createTextNode(node.textContent.slice(last)));
      node.replaceWith(fragment);
    }
  }

  #createYouTubeIframe(videoId) {
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}`;
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    iframe.className = 'bbd-sizing-guide-modal__video';
    return iframe;
  }

  #extractYouTubeId(url) {
    try {
      const parsed = new URL(url);
      if (parsed.hostname === 'youtu.be') return parsed.pathname.slice(1);
      if (parsed.hostname.endsWith('youtube.com')) return parsed.searchParams.get('v');
    } catch {
      // not a valid URL
    }
    return null;
  }

  openSizeGuide() {
    const dialog = this.querySelector('.bbd-sizing-guide-modal');
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (!dialog.dataset.embedsDone) {
      this.#embedYouTubeLinks(dialog);
      dialog.dataset.embedsDone = '1';
    }
    dialog.addEventListener('keydown', this.#onSizeGuideKeyDown);
    dialog.addEventListener('cancel', this.#onSizeGuideCancel);
    dialog.showModal();
  }

  closeSizeGuide() {
    const dialog = this.querySelector('.bbd-sizing-guide-modal');
    if (!(dialog instanceof HTMLDialogElement)) return;
    dialog.removeEventListener('keydown', this.#onSizeGuideKeyDown);
    dialog.removeEventListener('cancel', this.#onSizeGuideCancel);
    this.#closeDialog(dialog);
  }

  #onSizeGuideKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    event.stopPropagation();
  };

  #onSizeGuideCancel = (event) => {
    event.preventDefault();
    event.target.removeEventListener('keydown', this.#onSizeGuideKeyDown);
    event.target.removeEventListener('cancel', this.#onSizeGuideCancel);
    this.#closeDialog(event.target);
  };

  closeSizeGuideOnBackdrop(event) {
    const dialog = this.querySelector('.bbd-sizing-guide-modal');
    if (!(dialog instanceof HTMLDialogElement)) return;
    const rect = dialog.getBoundingClientRect();
    const clickedOutside =
      event.clientX < rect.left ||
      event.clientX > rect.right ||
      event.clientY < rect.top ||
      event.clientY > rect.bottom;
    if (clickedOutside) {
      event.stopPropagation();
      this.#closeDialog(dialog);
    }
  }

  #closeDialog(dialog) {
    dialog.classList.add('dialog-closing');
    dialog.addEventListener(
      'animationend',
      () => {
        dialog.classList.remove('dialog-closing');
        dialog.close();
      },
      { once: true }
    );
  }

  addToCart() {
    const variantId = this.refs.variantInput?.value;
    if (!variantId) return;

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    }

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        Accept: 'application/javascript'
      },
      body: formData
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        if (data.status) {
          console.error('Cart error:', data.description);
          return;
        }
        // Read product metafields from picker JSON block to pass along for GA4 tracking
        let pickerMeta = {};
        try {
          pickerMeta = JSON.parse(this.querySelector('script[type="application/json"]')?.textContent || '{}');
        } catch {
          // ignore
        }
        // Trigger cart drawer or redirect
        this.dispatchEvent(
          new CartUpdateEvent(data, this.id, {
            variantId,
            product_variant_name: pickerMeta.product_variant_name,
            product_information_name: pickerMeta.product_information_name,
            anchor_collection_title: pickerMeta.anchor_collection_title,
            category_name: pickerMeta.category_name,
            gender: pickerMeta.gender
          })
        );
        this.close();
      })
      .catch((error) => {
        console.error('Error:', error);
      });
  }
}

if (!customElements.get('bbd-variant-picker')) {
  customElements.define('bbd-variant-picker', BbdVariantPicker);
}
